import React, { useState, useEffect, useCallback } from 'react';
import {
    X, Users, Building, Briefcase, RefreshCw, BarChart3,
    Target, Clock, CheckCircle, AlertTriangle, BookOpen,
    ClipboardList, Shield, TrendingUp, TrendingDown,
    ChevronDown, ArrowRight, Award, AlertCircle, GitCompare, Download,
    Maximize2, Minimize2
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    ProgressRing,
    BarChart,
    DonutChart
} from '../Analytics';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';

const TABS = [
    { id: 'team', label: 'Team', icon: Users },
    { id: 'department', label: 'Department', icon: Building },
    { id: 'role', label: 'Role', icon: Briefcase },
    { id: 'compare', label: 'Compare', icon: GitCompare }
];

const GroupAnalyticsDrawer = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState('team');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState({ teams: [], departments: [], positions: [] });
    const [selectedGroup, setSelectedGroup] = useState('');
    const [data, setData] = useState(null);
    const [groupInfo, setGroupInfo] = useState(null);

    // Compare state
    const [compareType, setCompareType] = useState('department');
    const [compareSelection, setCompareSelection] = useState([]);
    const [compareData, setCompareData] = useState(null);
    const [compareLoading, setCompareLoading] = useState(false);

    const { drawerWidth, isDragging, dragHandleProps, dragHandleElement, isFullscreen, toggleFullscreen } = useDrawerResize({
        minWidth: 500, maxWidth: 900, defaultWidth: 680,
        storageKey: 'groupAnalyticsDrawerWidth',
    });

    // Fetch filter options on open
    const fetchOptions = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await api.get('/analytics/filters', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOptions(res.data.filters || { teams: [], departments: [], positions: [] });
        } catch (err) {
            console.error('Failed to fetch filter options:', err);
        }
    }, []);

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'comprehensive', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'group-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Group analytics exported');
        } catch { toast.error('Export failed'); }
    };

    useEffect(() => {
        if (isOpen) {
            fetchOptions();
            document.body.style.overflow = 'hidden';
        } else {
            setData(null);
            setGroupInfo(null);
            setSelectedGroup('');
            setCompareData(null);
            setCompareSelection([]);
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen, fetchOptions]);

    // Close on Escape
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    // Fetch group analytics
    const fetchGroupData = useCallback(async (tab, groupValue) => {
        if (!groupValue) return;
        setLoading(true);
        setData(null);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            let res;
            if (tab === 'team') {
                res = await api.get(`/analytics/teams/${groupValue}`, { headers });
                setGroupInfo(res.data.team);
            } else if (tab === 'department') {
                res = await api.get(`/analytics/departments/${encodeURIComponent(groupValue)}`, { headers });
                setGroupInfo(res.data.department);
            } else if (tab === 'role') {
                res = await api.get(`/analytics/roles/${encodeURIComponent(groupValue)}`, { headers });
                setGroupInfo(res.data.role);
            }
            setData(res?.data?.analytics || null);
        } catch (err) {
            console.error('Failed to fetch group analytics:', err);
            toast.error('Failed to load analytics');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Auto-fetch when group is selected
    useEffect(() => {
        if (activeTab !== 'compare' && selectedGroup) {
            fetchGroupData(activeTab, selectedGroup);
        }
    }, [activeTab, selectedGroup, fetchGroupData]);

    // Reset selection when tab changes is handled in the tab onClick

    // Fetch comparison data
    const fetchComparison = async () => {
        if (compareSelection.length < 2) {
            toast.error('Select at least 2 groups to compare');
            return;
        }
        setCompareLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            let url = `/analytics/compare?type=${compareType}`;
            if (compareType === 'team') {
                url += `&ids=${compareSelection.join(',')}`;
            } else {
                url += `&names=${compareSelection.map(encodeURIComponent).join(',')}`;
            }
            const res = await api.get(url, { headers });
            setCompareData(res.data.groups || []);
        } catch (err) {
            console.error('Comparison error:', err);
            toast.error('Failed to load comparison');
        } finally {
            setCompareLoading(false);
        }
    };

    const toggleCompareSelection = (val) => {
        setCompareSelection(prev =>
            prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
        );
    };

    const formatTime = (min) => {
        if (!min) return '-';
        if (min < 60) return `${Math.round(min)}m`;
        return `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`;
    };

    const getDropdownOptions = () => {
        if (activeTab === 'team') return options.teams.map(t => ({ value: t.id, label: t.team_name }));
        if (activeTab === 'department') return options.departments.map(d => ({ value: d, label: d }));
        if (activeTab === 'role') return options.positions.map(p => ({ value: p, label: p }));
        return [];
    };

    const getCompareOptions = () => {
        if (compareType === 'team') return options.teams.map(t => ({ value: String(t.id), label: t.team_name }));
        if (compareType === 'department') return options.departments.map(d => ({ value: d, label: d }));
        if (compareType === 'role') return options.positions.map(p => ({ value: p, label: p }));
        return [];
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity" onClick={onClose} />
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col animate-slide-in-right"
                style={{
                    width: isFullscreen ? '100vw' : `${drawerWidth}px`,
                    maxWidth: '100vw',
                    transition: isDragging ? 'none' : 'width 0.3s ease',
                }}
            >
                {/* ── Drag handle (left edge) — hidden in fullscreen ── */}
                {!isFullscreen && (
                    <div {...dragHandleProps}>
                        <div {...dragHandleElement} />
                    </div>
                )}

                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Group Analytics</h2>
                            <p className="text-sm text-gray-500">Team, department &amp; role insights</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="px-6 bg-white border-b border-[#E5E1DD] flex-shrink-0">
                    <div className="flex gap-1">
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    if (activeTab !== tab.id) {
                                        setActiveTab(tab.id);
                                        setSelectedGroup('');
                                        setData(null);
                                        setGroupInfo(null);
                                    }
                                }}
                                className={`flex items-center gap-1.5 py-3 px-3 border-b-2 transition-colors text-sm font-medium ${activeTab === tab.id
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide min-w-0">
                    {activeTab !== 'compare' ? (
                        <>
                            {/* Group Selector */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select {activeTab === 'team' ? 'Team' : activeTab === 'department' ? 'Department' : 'Role'}
                                </label>
                                <select
                                    value={selectedGroup}
                                    onChange={(e) => setSelectedGroup(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                                >
                                    <option value="">Choose...</option>
                                    {getDropdownOptions().map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {loading ? (
                                <div className="flex flex-col items-center justify-center h-48 space-y-3">
                                    <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                                    <p className="text-gray-500 font-medium">Loading analytics...</p>
                                </div>
                            ) : data ? (
                                <AnalyticsContent data={data} groupInfo={groupInfo} activeTab={activeTab} formatTime={formatTime} />
                            ) : selectedGroup ? (
                                <div className="text-center py-12 text-gray-500">No data available for this group.</div>
                            ) : (
                                <div className="text-center py-16 text-gray-400">
                                    <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                                    <p className="font-medium text-gray-600">Select a group to view analytics</p>
                                </div>
                            )}
                        </>
                    ) : (
                        /* Compare Tab */
                        <CompareView
                            compareType={compareType}
                            setCompareType={setCompareType}
                            compareSelection={compareSelection}
                            toggleCompareSelection={toggleCompareSelection}
                            setCompareSelection={setCompareSelection}
                            getCompareOptions={getCompareOptions}
                            fetchComparison={fetchComparison}
                            compareLoading={compareLoading}
                            compareData={compareData}
                            formatTime={formatTime}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── Analytics Content ───────────────────────────────────────────────── */
const AnalyticsContent = ({ data, groupInfo, activeTab, formatTime }) => {
    const tm = data.taskMetrics || {};
    const cm = data.courseMetrics || {};
    const sm = data.surveyMetrics || {};
    const comp = data.complianceMetrics || {};
    const risk = data.riskMetrics || {};
    const lc = data.lifecycleBreakdown || {};

    return (
        <div className="space-y-6">
            {/* Group Header */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {groupInfo?.name || groupInfo?.team_name || '-'}
                        </h3>
                        {groupInfo?.description && (
                            <p className="text-sm text-gray-500 mt-0.5">{groupInfo.description}</p>
                        )}
                    </div>
                    <div className="text-right">
                        <p className="text-3xl font-bold text-blue-600">{data.employeeCount}</p>
                        <p className="text-xs text-gray-500">Employees</p>
                    </div>
                </div>

                {/* Lifecycle Pills */}
                <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                    {[
                        { label: 'Preboarding', count: lc.preboarding, color: 'bg-blue-50 text-blue-800' },
                        { label: 'Onboarding', count: lc.onboarding, color: 'bg-blue-100 text-blue-800' },
                        { label: 'Active', count: lc.active, color: 'bg-blue-100 text-blue-800' }
                    ].map(item => (
                        <span key={item.label} className={`px-3 py-1.5 rounded-full text-sm font-medium ${item.color}`}>
                            {item.label}: {item.count || 0}
                        </span>
                    ))}
                </div>
            </div>

            {/* Task Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <StatCard title="Completion Rate" value={`${tm.completionRate || 0}%`} icon={Target}
                    color={tm.completionRate >= 80 ? 'green' : tm.completionRate >= 50 ? 'amber' : 'red'} />
                <StatCard title="Tasks Assigned" value={tm.totalAssigned || 0} icon={BarChart3} color="blue" />
                <StatCard title="Overdue" value={tm.overdue || 0} icon={AlertTriangle}
                    color={tm.overdue > 0 ? 'red' : 'green'} />
                <StatCard title="Avg Time" value={formatTime(tm.avgCompletionMinutes)} icon={Clock} color="blue" />
            </div>

            {/* Quiz & Course Row */}
            <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Award className="w-4 h-4 text-blue-500" /> Quiz Performance
                    </h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Avg Score</span>
                            <span className="font-semibold">{tm.avgQuizScore !== null ? `${tm.avgQuizScore}%` : '-'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Pass Rate</span>
                            <span className="font-semibold">{tm.quizPassRate !== null ? `${tm.quizPassRate}%` : '-'}</span>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-blue-500" /> Courses
                    </h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Enrolled</span>
                            <span className="font-semibold">{cm.totalEnrollments || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Completed</span>
                            <span className="font-semibold text-blue-600">{cm.completed || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Avg Progress</span>
                            <span className="font-semibold">{cm.avgProgress || 0}%</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Survey & Compliance Row */}
            <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-blue-500" /> Surveys
                    </h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Response Rate</span>
                            <span className="font-semibold">{sm.responseRate || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Avg Sentiment</span>
                            <span className="font-semibold">{sm.avgSentiment != null ? sm.avgSentiment.toFixed(2) : '-'}</span>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-500" /> Compliance
                    </h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Compliance Rate</span>
                            <span className={`font-semibold ${comp.complianceRate >= 90 ? 'text-blue-600' : 'text-red-600'}`}>
                                {comp.complianceRate ?? 100}%
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Active Violations</span>
                            <span className={`font-semibold ${comp.activeViolations > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                                {comp.activeViolations ?? 0}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Risk Distribution */}
            {risk.avgScore !== null && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h4 className="font-semibold text-gray-800 mb-3">Risk Distribution</h4>
                    <div className="flex items-center gap-6">
                        <ProgressRing value={100 - (risk.avgScore || 0)} size={80} label="Health" />
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
                            <div className="bg-red-50 rounded-lg p-3">
                                <p className="text-xl font-bold text-red-600">{risk.highRisk || 0}</p>
                                <p className="text-xs text-red-700">High Risk</p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-3">
                                <p className="text-xl font-bold text-blue-600">{risk.mediumRisk || 0}</p>
                                <p className="text-xs text-blue-700">Medium</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                                <p className="text-xl font-bold text-blue-600">{risk.lowRisk || 0}</p>
                                <p className="text-xs text-blue-700">Low Risk</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Top Performers */}
            {data.topPerformers?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-500" />
                        <h4 className="font-semibold text-gray-800">Top Performers</h4>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {data.topPerformers.map((emp, i) => (
                            <div key={emp.id} className="px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">{emp.name}</p>
                                        <p className="text-xs text-gray-500">{emp.position || '-'}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-semibold text-blue-600">{emp.completionRate}%</span>
                                    {emp.avgScore !== null && <p className="text-xs text-gray-500">Score: {emp.avgScore}%</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Struggling Employees */}
            {data.strugglingEmployees?.length > 0 && (
                <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
                    <div className="p-4 border-b border-red-100 bg-red-50 flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-red-500" />
                        <h4 className="font-semibold text-red-800">Needs Attention</h4>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {data.strugglingEmployees.map(emp => (
                            <div key={emp.id} className="px-4 py-3 flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-900 text-sm">{emp.name}</p>
                                    <p className="text-xs text-gray-500">{emp.position || '-'}</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-semibold text-red-600">{emp.overdue} overdue</span>
                                    <p className="text-xs text-gray-500">{emp.completionRate}% complete</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* AI Panel */}
            <AnalyticsAIPanel
                analyticsContext={{
                    type: 'group',
                    title: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Analytics`,
                    data: data,
                }}
            />

            {/* Notification Settings */}
            <NotificationSettingsPanel
                analyticsContext={{
                    type: 'group',
                    title: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Analytics`,
                    data: data,
                }}
            />
        </div>
    );
};

/* ─── Compare View ────────────────────────────────────────────────────── */
const CompareView = ({
    compareType, setCompareType, compareSelection, toggleCompareSelection,
    setCompareSelection, getCompareOptions, fetchComparison, compareLoading, compareData, formatTime
}) => {
    const options = getCompareOptions();
    const colors = ['#3B82F6', '#60A5FA', '#2563EB', '#93C5FD', '#EF4444', '#BFDBFE'];

    return (
        <div className="space-y-6">
            {/* Type Selector */}
            <div className="flex bg-gray-100 rounded-lg p-1">
                {['team', 'department', 'role'].map(t => (
                    <button
                        key={t}
                        onClick={() => { setCompareType(t); setCompareSelection([]); }}
                        className={`flex-1 py-2 rounded-md text-sm font-medium transition-all capitalize ${compareType === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {/* Selection Chips */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select groups to compare (2+)</label>
                <div className="flex flex-wrap gap-2">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => toggleCompareSelection(opt.value)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${compareSelection.includes(opt.value)
                                ? 'bg-blue-100 border-blue-300 text-blue-800'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {compareSelection.length >= 2 && (
                <button
                    onClick={fetchComparison}
                    disabled={compareLoading}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {compareLoading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <>
                            <GitCompare className="w-4 h-4" /> Compare Groups
                        </>
                    )}
                </button>
            )}

            {/* Comparison Results */}
            {compareData && compareData.length > 0 && (
                <div className="space-y-5">
                    {/* Employee Count */}
                    <CompareChart
                        title="Employee Count"
                        groups={compareData}
                        getValue={g => g.analytics.employeeCount}
                        colors={colors}
                    />

                    {/* Completion Rate */}
                    <CompareChart
                        title="Task Completion Rate"
                        groups={compareData}
                        getValue={g => g.analytics.taskMetrics?.completionRate || 0}
                        suffix="%"
                        colors={colors}
                    />

                    {/* Avg Quiz Score */}
                    <CompareChart
                        title="Avg Quiz Score"
                        groups={compareData}
                        getValue={g => g.analytics.taskMetrics?.avgQuizScore || 0}
                        suffix="%"
                        colors={colors}
                    />

                    {/* Course Progress */}
                    <CompareChart
                        title="Avg Course Progress"
                        groups={compareData}
                        getValue={g => g.analytics.courseMetrics?.avgProgress || 0}
                        suffix="%"
                        colors={colors}
                    />

                    {/* Compliance Rate */}
                    <CompareChart
                        title="Compliance Rate"
                        groups={compareData}
                        getValue={g => g.analytics.complianceMetrics?.complianceRate ?? 100}
                        suffix="%"
                        colors={colors}
                    />

                    {/* Survey Response Rate */}
                    <CompareChart
                        title="Survey Response Rate"
                        groups={compareData}
                        getValue={g => g.analytics.surveyMetrics?.responseRate || 0}
                        suffix="%"
                        colors={colors}
                    />

                    {/* Overdue Tasks */}
                    <CompareChart
                        title="Overdue Tasks"
                        groups={compareData}
                        getValue={g => g.analytics.taskMetrics?.overdue || 0}
                        colors={colors}
                    />
                </div>
            )}
        </div>
    );
};

/* ─── Simple Horizontal Bar Comparison ────────────────────────────────── */
const CompareChart = ({ title, groups, getValue, suffix = '', colors }) => {
    const values = groups.map(g => getValue(g));
    const max = Math.max(...values, 1);

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h4 className="font-semibold text-gray-800 text-sm mb-3">{title}</h4>
            <div className="space-y-2">
                {groups.map((g, i) => (
                    <div key={g.name} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-24 truncate">{g.name}</span>
                        <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden relative">
                            <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                    width: `${max > 0 ? (values[i] / max) * 100 : 0}%`,
                                    backgroundColor: colors[i % colors.length],
                                    minWidth: values[i] > 0 ? '8px' : '0'
                                }}
                            />
                        </div>
                        <span className="text-sm font-semibold text-gray-700 min-w-[48px] text-right">{values[i]}{suffix}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default GroupAnalyticsDrawer;
